import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Lock, ShieldCheck, Save, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import SharedPrevisionnelGrids, { EMPTY_PREVISIONNEL, fromPrevisionnelData } from "@/components/memo/SharedPrevisionnelGrids";
import { fetchPortail, fetchPortailPrevisionnel, savePortailPrevisionnel, magicFillPortail } from "@/lib/portail";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const fmtDate = (v) => { const d = v ? new Date(v) : null; return d && !isNaN(d) ? d.toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }) : null; };

function Header({ info }) {
    return (
        <header className="bg-primary text-primary-foreground">
            <div className="mx-auto max-w-6xl px-6 py-8 flex flex-wrap items-end justify-between gap-6">
                <img src="/logos/logo-acs-blanc.webp" alt="ACS Corporate" className="h-10 w-auto -ml-2" />
                {info && (
                    <div className="text-right">
                        <p className="text-xs uppercase tracking-[0.22em] text-white/50">Formulaire prévisionnel sécurisé</p>
                        <p data-testid="client-prev-mission" className="mt-1 font-display text-xl">{info.nom_mission || "Mission"}</p>
                        <p className="text-sm text-white/70">{info.entreprise} · SIREN {info.siren}</p>
                    </div>
                )}
            </div>
        </header>
    );
}

/** Portail « Service Comptable » : collecte pure via les grilles partagées. Aucun livrable, aucune mention de procédure. */
export default function ClientPrevisionnel() {
    const { token } = useParams();
    const [info, setInfo] = useState(null);
    const [error, setError] = useState("");
    const [email, setEmail] = useState(sessionStorage.getItem(`portail-email-${token}`) || "");
    const [unlocked, setUnlocked] = useState(false);
    const [data, setData] = useState(EMPTY_PREVISIONNEL);
    const [dateConseil, setDateConseil] = useState(null);
    const [updatedAt, setUpdatedAt] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let cancelled = false;
        fetchPortail(token).then((i) => { if (!cancelled) setInfo(i); }).catch((e) => { if (!cancelled) setError(e.message); });
        return () => { cancelled = true; };
    }, [token]);

    async function unlock(e) {
        e.preventDefault();
        const v = email.trim().toLowerCase();
        if (!EMAIL_RE.test(v)) return toast.error("Adresse e-mail invalide");
        try {
            const p = await fetchPortailPrevisionnel(token, v);
            setData(fromPrevisionnelData(p)); setDateConseil(p.date_debut_conseil || null); setUpdatedAt(p.updated_at);
            setEmail(v); sessionStorage.setItem(`portail-email-${token}`, v); setUnlocked(true);
        } catch (err) { toast.error(err.message); }
    }

    async function save() {
        setSaving(true);
        try {
            const r = await savePortailPrevisionnel(token, email, { revenus: data.revenus, charges_exploitation: data.charges_exploitation, charges: data.charges, salaires: data.salaires });
            setUpdatedAt(r.updated_at);
            toast.success("Prévisionnel enregistré", { description: "Merci, vos données ont été transmises au cabinet." });
        } catch (e) { toast.error("Enregistrement impossible", { description: e.message }); }
        finally { setSaving(false); }
    }

    if (error) {
        return (
            <div data-testid="client-prev-page" className="min-h-screen bg-slate-50 text-foreground"><Header />
                <main className="mx-auto max-w-lg px-6 py-20 text-center space-y-4"><AlertTriangle className="h-8 w-8 mx-auto text-destructive" strokeWidth={1.5} /><h1 className="font-display text-2xl">Lien indisponible</h1><p data-testid="client-prev-error" className="text-sm text-muted-foreground">{error}</p></main>
            </div>
        );
    }

    const label = fmtDate(dateConseil);
    return (
        <div data-testid="client-prev-page" className="min-h-screen bg-slate-50 text-foreground">
            <Header info={info} />
            <main className="w-full px-6 py-10 space-y-8">
                {!unlocked ? (
                    <form onSubmit={unlock} data-testid="client-prev-unlock-form" className="max-w-md space-y-6">
                        <div className="h-11 w-11 rounded-full bg-accent flex items-center justify-center"><Lock className="h-5 w-5 text-primary" strokeWidth={1.75} /></div>
                        <div><h1 className="font-display text-3xl">Identifiez-vous</h1><p className="mt-2 text-sm text-muted-foreground">Indiquez votre adresse e-mail pour accéder au formulaire prévisionnel. Chaque saisie est tracée nominativement pour le cabinet.</p></div>
                        <Input data-testid="client-prev-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="prenom.nom@entreprise.fr" className="h-11 bg-card" required />
                        <Button type="submit" data-testid="client-prev-unlock-button" disabled={!info} className="rounded-full h-11 px-6 bg-primary hover:bg-primary/90 text-primary-foreground"><ShieldCheck className="h-4 w-4" strokeWidth={2} /> Accéder au formulaire</Button>
                    </form>
                ) : (
                    <section data-testid="client-previsionnel" className="space-y-6">
                        <p data-testid="client-prev-identity" className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Connecté en tant que {email}</p>
                        <div data-testid="client-prev-consigne" className="rounded-xl border-2 border-red-200 bg-red-50 px-5 py-4 flex items-start gap-3 shadow-sm">
                            <AlertTriangle className="h-6 w-6 text-red-700 shrink-0" strokeWidth={2} />
                            <div>
                                <p className="text-xs font-bold uppercase tracking-[0.16em] text-red-700">⚠️ Consigne à lire avant toute saisie</p>
                                <p className="mt-1 text-base font-semibold text-red-700">{label ? <>Veuillez saisir vos données prévisionnelles à partir du <span data-testid="client-prev-date-conseil" className="underline decoration-2">{label}</span> (6 mois glissants).</> : "Date de démarrage à confirmer par votre conseil — ne saisissez pas avant confirmation."}</p>
                            </div>
                        </div>
                        <div className="flex flex-wrap items-center justify-between gap-4">
                            <div><h2 className="font-display text-2xl">Données prévisionnelles — 6 mois</h2><p className="text-xs text-muted-foreground">Revenus attendus, charges, masse salariale (montants HT, taux de TVA applicable). {updatedAt && <>Dernier enregistrement : {new Date(updatedAt).toLocaleString("fr-FR")}</>}</p></div>
                            <Button data-testid="client-prev-save" onClick={save} disabled={saving} className="rounded-full h-10 px-5 bg-primary hover:bg-primary/90 text-primary-foreground"><Save className="h-4 w-4" strokeWidth={2} /> {saving ? "Enregistrement…" : "Enregistrer le prévisionnel"}</Button>
                        </div>
                        <SharedPrevisionnelGrids value={data} onChange={setData} prefix="prev" onMagicFill={(section, i, documentIds) => magicFillPortail(token, email, section, i, documentIds)} />
                        <div className="flex justify-end">
                            <Button data-testid="client-prev-save-bottom" onClick={save} disabled={saving} className="rounded-full h-10 px-5 bg-primary hover:bg-primary/90 text-primary-foreground"><Save className="h-4 w-4" strokeWidth={2} /> {saving ? "Enregistrement…" : "Enregistrer le prévisionnel"}</Button>
                        </div>
                    </section>
                )}
            </main>
        </div>
    );
}
